import { InputGroup, InputGroupInput } from '@solinkcorp/solink-ui/components/InputGroup';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { Clock } from 'lucide-react';
import { type KeyboardEvent } from 'react';
import { timeInputLang } from './datePickerTime';

/**
 * The time field in the location date picker.
 * Hide the browser's clock icon and keep the field font on the time segments.
 *
 * No fill. The dark group fill is surface grey, and on the video that is a grey chip.
 * The location picker sits on that same surface, so a transparent field matches it.
 */
const TIME_INPUT_CLASS =
  'field-sizing-content h-full w-auto min-w-fit flex-none appearance-none px-0 py-0 text-foreground-primary body-sm [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none [&::-webkit-datetime-edit-ampm-field]:[font:inherit] [&::-webkit-datetime-edit-fields-wrapper]:[font:inherit] [&::-webkit-datetime-edit-hour-field]:[font:inherit] [&::-webkit-datetime-edit-minute-field]:[font:inherit] [&::-webkit-datetime-edit-second-field]:[font:inherit] [&::-webkit-datetime-edit-text]:[font:inherit] [&::-webkit-datetime-edit]:text-foreground-primary [&::-webkit-datetime-edit]:[font:inherit]';

export function DatePickerTimeInput({
  value,
  onChange,
  onCommit,
  disabled,
  invalid,
  ariaInvalid,
  ariaDescribedBy,
  ariaLabel,
  dataTestId,
  className,
  timezone,
  timezoneTestId,
}: {
  value: string;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  ariaLabel: string;
  dataTestId?: string;
  className?: string;
  /** Short zone name. It sits after the AM/PM text inside the field. */
  timezone?: string;
  timezoneTestId?: string;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    onCommit(event.currentTarget.value);
  };

  return (
    <InputGroup
      className={cn(
        // InputGroup uses radius-md. This field uses the input radius.
        // px-3 matches the date field padding. The gap before the timezone is a space
        // character, so it matches the space between the time and AM/PM.
        'h-7 w-fit shrink-0 gap-0 rounded bg-transparent px-3 text-foreground-primary dark:bg-transparent',
        className,
        invalid &&
          'border-destructive ring-2 ring-destructive has-[[data-slot=input-group-control]:focus-visible]:border-destructive has-[[data-slot=input-group-control]:focus-visible]:ring-destructive',
      )}
      data-disabled={disabled || undefined}
    >
      <Clock className="mr-2 size-4 shrink-0" aria-hidden />
      <InputGroupInput
        type="time"
        step={1}
        lang={timeInputLang()}
        value={value}
        disabled={disabled}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => onCommit(event.target.value)}
        onKeyDown={handleKeyDown}
        className={TIME_INPUT_CLASS}
      />
      {timezone != null && (
        // The zone copies the input size. It cannot be edited, so it uses the inactive text color.
        <span
          className="shrink-0 whitespace-pre text-base text-interaction-secondary-content-inactive body-sm md:text-sm"
          data-testid={timezoneTestId}
        >
          {` ${timezone}`}
        </span>
      )}
    </InputGroup>
  );
}
